import { useCallback } from 'react';

import conf from '@/config';
import { useSessionContext } from '@/features/game/hooks/use-session-context';
import {
  getPlatformType,
  resolveProviderRef,
} from '@/features/game/platform-helpers';
import { fetchFgpGameData } from '@/features/game/store/thunks/fetch-fgp-game-data';
import {
  GameDetailsInput,
  PokerProviderNamesToFgp,
  ProviderNameToFgp,
} from '@/features/game/types';
import { getChannel } from '@/features/game-context/platform-helpers';
import { Product } from '@/features/game-context/types';
import { useAppDispatch } from '@/store/hooks';

export const useFgpGame = () => {
  const dispatch = useAppDispatch();
  const sessionContext = useSessionContext();
  const channel = getChannel();

  const fetchGameData = useCallback(
    (input: GameDetailsInput) => {
      const platform = getPlatformType();

      const product = PokerProviderNamesToFgp.includes(
        ProviderNameToFgp[input.provider],
      )
        ? Product.Poker.toUpperCase()
        : undefined;

      // resolveProviderRef returns undefined only when desktop, mobile, and
      // legacy codes are all absent — use-game.ts guards on
      // (providerRefDesktop || providerRefMobile || providerRef) before
      // building the input, so this is always defined at this point.
      const providerRef = resolveProviderRef(
        platform,
        input.providerRefDesktop,
        input.providerRefMobile,
        input.providerRef,
      )!;

      if (sessionContext && input.gameUid) {
        dispatch(
          fetchFgpGameData({
            sessionCode: sessionContext?.fdSessionCode,
            authToken: input.authToken,
            loginToken: input.loginToken,
            platform,
            channel,
            userId: input.userId,
            provider: input.provider,
            providerRef,
            ip: input.ipAddress,
            gameUid: input.gameUid,
            fdState: sessionContext?.fdState,
            lobbyUrl: conf.lobbyUrl,
            geolocationToken: input.geolocationToken,
            jackpotMode: input?.jackpotMode,
            ...(product && { product }),
            ...(input?.directLaunchParams && {
              slices: {
                directLaunchParams: input?.directLaunchParams,
              },
            }),
          }),
        );
      }
    },
    [sessionContext, dispatch, channel],
  );

  return { fetchGameData };
};
